'use client'
import { PortableText, PortableTextComponents, SanityImageAssetDocument } from "next-sanity";
import { PortableTextObject, set } from "sanity";
import { styles, GalleryVariants } from "./Gallery.css";
import { AppList } from "../AppList";
import { AppText } from "../AppText";
import { SanityImage } from "../SanityImage";
import { Tag } from "../Tag";
import { AppTitle } from "../AppTitle";
import clsx from "clsx";
import { motion, AnimatePresence, stagger } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Button } from "../Button";
import { Arrow } from "@otomoro/components/icons/Arrow";

type GalleryItem = {
  thumbnail: SanityImageAssetDocument;
  label: string;
  headline?: string;
  mainContent: PortableTextObject;
}

interface GalleryProps extends GalleryVariants {
  tag: string;
  gallery: GalleryItem[];
	hasArrows?: boolean;
}

export const Gallery: React.FC<GalleryProps> = ({ tag, gallery, hasArrows = false }) => {
  const [openIdx, setOpenIdx] = useState<number>(0)
	const navRef = useRef<HTMLDivElement>(null)

  const { base, card, figure, image, contentList, contentContainer, content, nav, navList } = styles()

	useEffect(() => {
			if (!hasArrows) return;
			const els = navRef.current?.querySelectorAll('.button-scroll') as HTMLElement[] | undefined;

			if (!els?.[openIdx]) return;

			const offsetLeft = els?.[openIdx]?.offsetLeft;

			navRef.current?.scrollTo({
				left: offsetLeft - 30,
				behavior: 'smooth'
			})
	}, [openIdx])

  const contentComponent = {
		block: {
			normal: ({ children }: any) => (
				<AppText as="p" size="base" weight="medium">
					{children}
				</AppText>
			),
		},
		marks: {
			highlight: ({ children }: any) => <span className="text-mainPink">{children}</span>,
		},
	} as PortableTextComponents

  return (
    <section className={base()}>
      <aside ref={navRef} className={clsx('nav-scroll', nav({hasArrows}))}>
        <AppList className={navList()}>
          {(gallery && gallery.length > 0) && gallery.map((item, index) => (
            <Button
              size="gallery"
              variant="withoutBg"
              fontSize="small"
              fontFamily="helvetica"
              arrow={false}
              key={index}
              onClick={() => setOpenIdx(index)}
              onFocus={() => setOpenIdx(index)}
              className={clsx('button-scroll', {['!bg-mainPink']: openIdx === index})}
            >
              {item.label ? item.label : item.headline}
            </Button>
          ))}
        </AppList>
      </aside>

			{hasArrows && (
				<div className="absolute top-4 right-2 left-2 md:left-auto md:top-auto md:right-4 md:bottom-4 flex justify-between pointer-events-none space-x-2 z-[2]">
					<Button
						className="pointer-events-auto"
						onClick={() => setOpenIdx(openIdx-1)}
						disabled={openIdx === 0}
						single
					>
						<Arrow orientation="left" />
						<span className="sr-only">Previous slide</span>
					</Button>
					<Button
						className="pointer-events-auto"
						onClick={() => setOpenIdx(openIdx+1)}
						disabled={openIdx === (gallery.length - 1)}
						single
					>
						<Arrow orientation="right" color="#fff" />
						<span className="sr-only">Next slide</span>
					</Button>
				</div>
			)}

      {(gallery && gallery.length > 0) && gallery.map((item, index) => (
        <AnimatePresence key={index} mode="wait" initial={false}>
          {openIdx === index && (
            <motion.div
              initial={{ transform: 'translateX(100vw)' }}
              animate={{ transform: 'translateX(0)' }}
              exit={{
                transform: 'translateX(-100vw)',
                transition: { duration: 0.8 }
              }}
              transition={{ duration: 0.6 }}
              className={clsx(figure(), {['!z-[1]']: openIdx === index})}
            >
              <motion.figure
                initial={{ transform: 'translateX(-100vw) scale(1.2)' }}
                animate={ { transform: 'translateX(0) scale(1)' }}
                exit={{
                  transform: 'translateX(100vw)',
                  transition: { duration: 0.8 }
                }}
                transition={{ duration: 0.6 }}
                className='size-full'
              >
                  <SanityImage
                    image={item.thumbnail.image}
                    alt={item.thumbnail.alt || ''}
                    key={index}
                    className={image()}
                    sizes='(max-width: 768px) 80vw, (max-width: 1200px) 50vw, 60vw'
                    priority={true}
                  />
              </motion.figure>
            </motion.div>
          )}
        </AnimatePresence>
      ))}

      <div className={card()}>
        <Tag as="strong" className="mb-1">{tag}</Tag>
        <AppList noWrapper className={contentList()}>
          {(gallery && gallery.length > 0) && gallery.map((item, index) => {
            const splitteHeadline = item.headline?.split(" ") || [];
            return (
              <AnimatePresence key={index} mode="wait" initial={false}>
                {openIdx === index && (
                  <motion.li
                    initial={{ height: 0 }}
                    animate={{ height: 'auto' }}
                    exit={{ height: 0 }}
                    transition={{ duration: 0.4 }}
                    className={contentContainer()}
                  >
                    {item.label && (
                      <AppTitle
                        as="strong"
                        variant="h1"
                        textTransform="uppercase"
                        className="overflow-hidden text-mainPink max-md:!text-h2-mobile"
                      >
                        <span key={index} className="inline-block overflow-hidden">
                          <motion.span
                            initial={{ transform: 'translateY(100%)' }}
                            animate={{ transform: 'translateY(0)' }}
                            exit={{
                              transform: 'translateY(-100%)',
                              transition: { duration: 0.2 }
                            }}
                            transition={{ duration: 0.3, delay: 0.25 }}
                            className="block break-words"
                          >
                            {item.label}
                          </motion.span>
                        </span>
                      </AppTitle>
                    )}
                    {item.headline && (
                      <AppTitle
                        as="h3"
                        variant="h1"
                        textTransform="uppercase"
                        className="overflow-hidden flex flex-wrap space-x-[5px] max-md:!text-h2-mobile font-bold"
                      >
                        {splitteHeadline.map((word: string, index: number) => {
                          const delay = index * 0.1 + 0.3;
                          return (
                            <span key={index} className="inline-block shrink-0 overflow-hidden">
                              <motion.span
                                initial={{ transform: 'translateY(100%)' }}
                                animate={{ transform: 'translateY(0)' }}
                                exit={{
                                  transform: 'translateY(-100%)',
                                  transition: { duration: 0.2 }
                                }}
                                transition={{ duration: 0.3, delay: delay }}
                                className="block break-words"
                              >
                                {word + " "}
                              </motion.span>
                            </span>
                          )
                        })}
                      </AppTitle>
                    )}
                    {item.mainContent && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{
                          opacity: 0,
                          transition: { duration: 0.3 }
                        }}
                        transition={{ duration: 0.3, delay: 0.3 }}
                        className={content()}
                      >
                        <PortableText value={item.mainContent} components={contentComponent} />
                      </motion.div>
                    )}
                  </motion.li>
                )}
              </AnimatePresence>
            )
          })}
        </AppList>
      </div>
    </section>
  )
}
